import { getActiveOrgId } from '@/lib/activeOrg';
import { supabase } from '@/lib/supabase';
import { SeoAgent, SeoAgentMode, SeoAgentTemplate } from '@/types/api/seo';

function mapRow(row: any): SeoAgent {
  return {
    id: row.id,
    organizationId: row.organization_id,
    projectId: row.project_id,
    name: row.name,
    description: row.description,
    track: row.track,
    mode: row.mode,
    scheduleCron: row.schedule_cron,
    scheduleLabel: row.schedule_label,
    fieldScope: row.field_scope ?? [],
    llmModel: row.llm_model,
    promptTemplate: row.prompt_template,
    monthlyTokenLimit: row.monthly_token_limit,
    tokensUsedThisMonth: row.tokens_used_this_month,
    targetPageIds: row.target_page_ids ?? [],
    monthlyCostLimitUsd:
      row.monthly_cost_limit_usd != null ? Number(row.monthly_cost_limit_usd) : null,
    costUsedThisMonthUsd: Number(row.cost_used_this_month_usd ?? 0),
    enabled: row.enabled,
    lastRunAt: row.last_run_at,
    lastRunStatus: row.last_run_status,
    totalRuns: row.total_runs,
    totalChangesApplied: row.total_changes_applied,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapTemplateRow(row: any): SeoAgentTemplate {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    track: row.track,
    scheduleCron: row.schedule_cron,
    scheduleLabel: row.schedule_label,
    fieldScope: row.field_scope ?? [],
    llmModel: row.llm_model,
    promptTemplate: row.prompt_template,
    sortOrder: row.sort_order,
  };
}

const seoAgentsService = {
  async getByProject(projectId: string): Promise<SeoAgent[]> {
    const { data, error } = await supabase
      .from('seo_agents')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async getById(id: string): Promise<SeoAgent> {
    const { data, error } = await supabase.from('seo_agents').select('*').eq('id', id).single();

    if (error) throw error;
    return mapRow(data);
  },

  async getTemplates(): Promise<SeoAgentTemplate[]> {
    const { data, error } = await supabase
      .from('seo_agent_templates')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) throw error;
    return (data ?? []).map(mapTemplateRow);
  },

  async initializeForProject(projectId: string): Promise<SeoAgent[]> {
    const orgId = getActiveOrgId();
    if (!orgId) throw new Error('No active organization');

    const templates = await this.getTemplates();

    const rows = templates.map((t) => ({
      organization_id: orgId,
      project_id: projectId,
      name: t.name,
      description: t.description,
      track: t.track,
      mode: 'shadow' as const,
      schedule_cron: t.scheduleCron,
      schedule_label: t.scheduleLabel,
      field_scope: t.fieldScope,
      llm_model: t.llmModel,
      prompt_template: t.promptTemplate,
      enabled: false,
    }));

    const { data, error } = await supabase.from('seo_agents').insert(rows).select();

    if (error) throw error;
    return (data ?? []).map(mapRow);
  },

  async updateMode(id: string, mode: SeoAgentMode): Promise<void> {
    const { error } = await supabase
      .from('seo_agents')
      .update({ mode, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  async toggleEnabled(id: string, enabled: boolean): Promise<void> {
    const { error } = await supabase
      .from('seo_agents')
      .update({ enabled, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  async updateTargetPages(id: string, pageIds: string[]): Promise<void> {
    const { error } = await supabase
      .from('seo_agents')
      .update({ target_page_ids: pageIds, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) throw error;
  },

  async updateConfig(
    id: string,
    config: {
      scheduleCron?: string | null;
      scheduleLabel?: string | null;
      llmModel?: string;
      monthlyTokenLimit?: number | null;
      monthlyCostLimitUsd?: number | null;
      promptTemplate?: string | null;
    }
  ): Promise<void> {
    const updates: Record<string, any> = { updated_at: new Date().toISOString() };
    if (config.scheduleCron !== undefined) updates.schedule_cron = config.scheduleCron;
    if (config.scheduleLabel !== undefined) updates.schedule_label = config.scheduleLabel;
    if (config.llmModel !== undefined) updates.llm_model = config.llmModel;
    if (config.monthlyTokenLimit !== undefined)
      updates.monthly_token_limit = config.monthlyTokenLimit;
    if (config.monthlyCostLimitUsd !== undefined)
      updates.monthly_cost_limit_usd = config.monthlyCostLimitUsd;
    if (config.promptTemplate !== undefined) updates.prompt_template = config.promptTemplate;

    const { error } = await supabase.from('seo_agents').update(updates).eq('id', id);

    if (error) throw error;
  },
};

export default seoAgentsService;
